import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaCheckDouble, FaTimes, FaSpinner } from 'react-icons/fa';
import {
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  subscribeToNotifications,
  getNotificationIcon,
  formatNotificationTime,
  type Notification
} from '../lib/notifications';
import { getCurrentUser } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function NotificationBell() {
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const isMountedRef = useRef(true);
  const loadingTimeoutRef = useRef<number | null>(null);

  const user = getCurrentUser();
  const navigate = useNavigate();

  // Keep isMounted flag accurate to avoid setState after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Load notifications + subscribe
  useEffect(() => {
    if (!user?.id) return;

    // initial load (silent spinner on bell)
    void loadNotifications();

    // also refresh the unread count (if your helper is fast)
    void refreshUnreadCount();

    // subscribe to realtime
    let unsubscribe: (() => void) | void;
    try {
      unsubscribe = subscribeToNotifications(user.id, (newNotification) => {
        if (!isMountedRef.current) return;
        setItems(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        setHasNewNotification(true);

        toast.success(newNotification.title, {
          icon: getNotificationIcon(newNotification.type) as any,
          duration: 4000
        });

        setTimeout(() => {
          if (isMountedRef.current) setHasNewNotification(false);
        }, 3000);
      });
    } catch (e) {
      console.error('subscribeToNotifications failed:', e);
    }

    return () => {
      try {
        if (typeof unsubscribe === 'function') unsubscribe();
      } catch (e) {
        // ignore
      }
    };
  }, [user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshUnreadCount = async () => {
    if (!user?.id) return;
    try {
      // If your helper supports userId, pass it; otherwise keep it without args
      const count = await getUnreadNotificationCount(/* user.id */);
      if (!isMountedRef.current) return;
      setUnreadCount(typeof count === 'number' ? count : 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadNotifications = async (showSpinner = false) => {
    if (!user?.id) return;

    if (showSpinner) setLoading(true);
    else {
      // If opening the dropdown with 0 items, show spinner
      if (items.length === 0) setLoading(true);
    }

    // Failsafe: ensure loading doesn't get stuck due to a hanging promise
    if (loadingTimeoutRef.current) window.clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = window.setTimeout(() => {
      if (isMountedRef.current) setLoading(false);
    }, 8000);

    try {
      // If your helper supports (userId, limit) use that, otherwise just (limit)
      const list = await (getUserNotifications.length >= 2
        ? (getUserNotifications as any)(user.id, 20)
        : getUserNotifications(20));

      if (!isMountedRef.current) return;

      const safeList: Notification[] = Array.isArray(list) ? list : [];
      setItems(safeList);
      // Single source of truth for unread
      setUnreadCount(safeList.reduce((acc, n) => acc + (n.is_read ? 0 : 1), 0));
    } catch (error) {
      console.error('Error loading notifications:', error);
      if (!isMountedRef.current) return;
      setItems([]);
      setUnreadCount(0);
    } finally {
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!user?.id) return;

    // Mark as read if needed
    if (!notification.is_read) {
      try {
        await markNotificationAsRead(notification.id);
        if (!isMountedRef.current) return;
        setItems(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    setIsOpen(false);

    // Navigate by type
    switch (notification.type) {
      case 'message':
        if (notification.related_user_id) {
          navigate('/messages', { state: { friendId: notification.related_user_id } });
        } else {
          navigate('/messages');
        }
        break;
      case 'friend_request':
        navigate('/friends', { state: { activeTab: 'requests' } });
        break;
      case 'friend_accepted':
        navigate('/friends');
        break;
      case 'new_song':
        navigate(notification.related_id ? `/songs/${notification.related_id}` : '/songs');
        break;
      case 'new_story':
        navigate(notification.related_id ? `/story/${notification.related_id}` : '/stories');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id || markingAllRead) return;

    setMarkingAllRead(true);
    try {
      await markAllNotificationsAsRead();
      if (!isMountedRef.current) return;
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    } finally {
      if (isMountedRef.current) setMarkingAllRead(false);
    }
  };

  const toggleDropdown = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) {
      // When opening, refresh data with a visible spinner
      void loadNotifications(true);
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      {/* Bell */}
      <button
        ref={bellRef}
        onClick={toggleDropdown}
        className={`relative p-2 rounded-full transition-all duration-300 ${
          isOpen
            ? 'bg-yellow-400/20 text-yellow-400'
            : 'hover:bg-white/10 text-white hover:text-yellow-400'
        }`}
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <motion.div
          animate={
            hasNewNotification
              ? { scale: [1, 1.2, 1], rotate: [0, 15, -15, 0] }
              : {}
          }
          transition={{ duration: 0.6 }}
        >
          <FaBell className="w-5 h-5" />
        </motion.div>

        {/* Unread badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.div
              className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold shadow-lg"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulse */}
        {hasNewNotification && (
          <motion.div
            className="absolute inset-0 rounded-full bg-yellow-400/30"
            initial={{ scale: 1, opacity: 0.7 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 1, repeat: 3 }}
          />
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            className="absolute mt-2 bg-gray-900 rounded-xl shadow-2xl border border-gray-700 z-50 max-h-96 overflow-hidden
                     md:right-0 md:w-80
                     max-md:right-1 max-md:w-70 max-md:max-w-[calc(100vw-1rem)]"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-700 bg-gray-800/50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center">
                  <FaBell className="w-4 h-4 mr-2 text-yellow-400" />
                  Notifications
                </h3>
                <div className="flex items-center space-x-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      disabled={markingAllRead}
                      className="text-xs text-yellow-400 hover:text-yellow-300 disabled:opacity-50 flex items-center"
                    >
                      {markingAllRead ? (
                        <FaSpinner className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <FaCheckDouble className="w-3 h-3 mr-1" />
                      )}
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 rounded-full hover:bg-gray-700 transition-colors"
                  >
                    <FaTimes className="w-3 h-3 text-gray-400" />
                  </button>
                </div>
              </div>
              {unreadCount > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <FaSpinner className="w-6 h-6 text-yellow-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Loading notifications...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaBell className="w-6 h-6 text-gray-500" />
                  </div>
                  <p className="text-gray-400 text-sm">No notifications yet</p>
                  <p className="text-gray-500 text-xs mt-1">
                    You'll see notifications here when you receive messages, friend requests, and more
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {items.map((notification) => (
                    <motion.div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-800/50 cursor-pointer transition-all duration-200 ${
                        !notification.is_read ? 'bg-yellow-400/5 border-l-2 border-yellow-400' : ''
                      }`}
                      onClick={() => void handleNotificationClick(notification)}
                      whileHover={{ x: 2 }}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          !notification.is_read ? 'bg-yellow-400/20' : 'bg-gray-700/50'
                        }`}>
                          <span className="text-sm">{getNotificationIcon(notification.type)}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className={`text-sm font-medium truncate ${
                              !notification.is_read ? 'text-white' : 'text-gray-300'
                            }`}>
                              {notification.title}
                            </h4>
                            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                              <span className="text-xs text-gray-500">
                                {formatNotificationTime(notification.created_at)}
                              </span>
                              {!notification.is_read && (
                                <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                              )}
                            </div>
                          </div>

                          <p className={`text-xs leading-relaxed ${
                            !notification.is_read ? 'text-gray-300' : 'text-gray-400'
                          }`}>
                            {notification.message}
                          </p>

                          {/* Related User (optional shape) */}
                          {notification.related_user && (
                            <div className="flex items-center mt-2 space-x-2">
                              <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-700">
                                {notification.related_user.profile_image ? (
                                  <img
                                    src={notification.related_user.profile_image}
                                    alt="User"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white">
                                    {(notification.related_user.email || '?').charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <span className="text-xs text-gray-400">
                                {notification.related_user.full_name || notification.related_user.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-3 border-t border-gray-700 bg-gray-800/30">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                  className="w-full text-center text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  View all notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
